import { useMemo, useState, useEffect, useCallback } from 'react';
import { itemListener } from '~/firebase/listeners/item';

import cartOptionCountSelector from '~/recoil/selectors/cartOptionCount';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import { useRecoilValue } from 'recoil';

import useStoreModifiers from '~/hooks/useStoreModifiers';

import getDateInventory from '~/utils/getDateInventory';

// types
import { Item, ItemWithId, ItemWithModifiers } from '~/types/store/item';
import { SnapshotFn } from '~/firebase/listeners/type';
import { ModifierWithRestInventoryOption } from '~/types/store/modifier';
import { CartItem } from '~/types/cart';

const defaultItem: ItemWithId = {
  id: '',
  name: '',
  description: '',
  image: '',
  tags: {},
  modifiers: [],
  price: 0,
  tax: 0,
  isAvailable: true,
  inventory: {
    default: 0,
    reserved: {},
    current: {},
  },
  containers: {},
  orderCountInStore: 0,
  orderCountInPlatform: {},
};

export default function useItem(storeId: string, itemId: string, itemInCart?: CartItem) {
  const modifiersMap = useStoreModifiers(storeId);
  const [item, setItem] = useState<ItemWithId>(defaultItem);
  const cartOptionCountMap = useRecoilValue(cartOptionCountSelector({ storeId }));
  const pickMealAt = useRecoilValue(pickMealAtAtom);

  const handleItemSnapshot = useCallback<SnapshotFn>(
    (doc) => {
      const data = doc.data() as Item | undefined;
      if (!doc.exists() || !data) {
        return;
      }

      setItem({
        id: doc.id,
        ...data,
      });
    },
    [setItem],
  );

  useEffect(() => {
    const unlisten = itemListener(storeId, itemId, handleItemSnapshot);

    return () => {
      unlisten();
    };
  }, [storeId, itemId, handleItemSnapshot]);

  const itemWithModifiers = useMemo<ItemWithModifiers>(() => {
    const itemInCartModifiers = itemInCart?.modifiers || [];
    const itemInCartCount = itemInCart?.count || 0;

    const itemInCartOptionsSet = new Set(
      itemInCartModifiers.flatMap(({ id, options }) =>
        options.map((option) => `${id}_${option.id}`),
      ),
    );

    const modifiers = item.modifiers.reduce<Array<ModifierWithRestInventoryOption>>(
      (acc, modifierId) => {
        const modifier = modifiersMap.get(modifierId);

        if (modifier) {
          const newOptions = modifier.options.map((option) => {
            const dateInventory = getDateInventory(option.inventory, pickMealAt);
            const optionCountInCart = cartOptionCountMap.get(`${modifier.id}_${option.id}`) || 0;

            // 如果是編輯，必須把當前編輯的品項的選項補回來
            return {
              ...option,
              restInventory:
                dateInventory -
                optionCountInCart +
                (itemInCartOptionsSet.has(`${modifier.id}_${option.id}`) ? itemInCartCount : 0),
            };
          });

          acc.push({ ...modifier, options: newOptions });
        }

        return acc;
      },
      [],
    );

    return {
      ...item,
      modifiers,
    };
  }, [modifiersMap, item, cartOptionCountMap, itemInCart, pickMealAt]);

  return itemWithModifiers;
}
